<script setup>
import { computed, onMounted, ref } from 'vue'
import axiosIns from '@/plugins/axios'

// ── Restaurant options (with overseer info) ─────────────────────────
const loadingOptions = ref(true)
const optionsError = ref('')
const restaurants = ref([])

const selectedIds = ref([])

onMounted(async () => {
  try {
    const res = await axiosIns.get('/admin/reports/restaurant-report/options')
    restaurants.value = res.data?.restaurants || []
  } catch (e) {
    optionsError.value = e.response?.data?.error || 'Could not load restaurants'
  } finally {
    loadingOptions.value = false
  }
})

const restaurantItems = computed(() => restaurants.value.map(r => ({
  value: r._id,
  title: r.market ? `${r.name} (${r.market})` : r.name,
})))

const selectedRestaurants = computed(() =>
  restaurants.value.filter(r => selectedIds.value.includes(r._id)),
)

// The people who oversee the selected restaurants (via their restaurant
// account UserGroup) — shown so Kapil knows who the report is for.
const overseers = computed(() => {
  const seen = new Map()
  for (const r of selectedRestaurants.value) {
    for (const o of r.overseers || []) {
      if (!seen.has(o.email)) seen.set(o.email, o)
    }
  }

  return [...seen.values()]
})

// Sibling restaurants under the same restaurant account that aren't selected
// yet — one click adds them so a report covers everything the person oversees
// (e.g. selecting Tre Dita suggests Miru for Kiran).
const siblingSuggestions = computed(() => {
  const selectedGroupIds = new Set(
    selectedRestaurants.value.map(r => r.restaurantUserGroupId).filter(Boolean),
  )
  const selectedOverseerEmails = new Set(
    selectedRestaurants.value.flatMap(r => (r.overseers || []).map(o => o.email)),
  )
  if (selectedOverseerEmails.size === 0 && selectedGroupIds.size === 0) return []

  return restaurants.value.filter(r => {
    if (selectedIds.value.includes(r._id)) return false
    if (r.restaurantUserGroupId && selectedGroupIds.has(r.restaurantUserGroupId)) return true

    return (r.overseers || []).some(o => selectedOverseerEmails.has(o.email))
  })
})

function addSibling(r) {
  selectedIds.value = [...selectedIds.value, r._id]
}

// ── Report options ──────────────────────────────────────────────────
const includeContact = ref(false)
const showThresholds = ref(false)
const minRequestsYtd = ref(5)
const minRequestsL3M = ref(3)

// ── Generate ────────────────────────────────────────────────────────
const generating = ref(false)
const generateError = ref('')
const lastGenerated = ref('')

async function generateReport() {
  if (selectedIds.value.length === 0) return
  generating.value = true
  generateError.value = ''
  lastGenerated.value = ''
  try {
    const res = await axiosIns.post('/admin/reports/restaurant-report', {
      restaurantIds: selectedIds.value,
      includeContact: includeContact.value,
      minRequestsYtd: minRequestsYtd.value,
      minRequestsL3M: minRequestsL3M.value,
    }, { responseType: 'blob', timeout: 120000 })

    const disposition = res.headers['content-disposition'] || ''
    const match = disposition.match(/filename="([^"]+)"/)
    const filename = match ? match[1] : 'rm-report.pdf'

    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
    lastGenerated.value = filename
  } catch (e) {
    // Blob error responses need decoding before we can show the message.
    let message = 'Report generation failed'
    try {
      const text = await e.response?.data?.text?.()
      if (text) message = JSON.parse(text).error || message
    } catch { /* keep default */ }
    generateError.value = message
  } finally {
    generating.value = false
  }
}
</script>

<template>
  <VContainer
    class="admin-reports px-md-6"
    style="max-width: 860px;"
  >
    <header class="mb-6">
      <h1 class="text-h4 mb-1">
        Restaurant Reports
      </h1>
      <p class="text-body-2 text-medium-emphasis mb-0">
        Generate a PDF performance report for a restaurant — summary, fans, booking
        stats, full booking list, and cross-RM leaderboards.
      </p>
    </header>

    <VAlert
      v-if="optionsError"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ optionsError }}
    </VAlert>

    <VCard>
      <VCardText>
        <!-- 1. Restaurants (the one decision that matters) -->
        <VAutocomplete
          v-model="selectedIds"
          :items="restaurantItems"
          :loading="loadingOptions"
          label="Restaurants"
          placeholder="Search restaurants…"
          multiple
          chips
          closable-chips
          clearable
          clear-on-select
          class="admin-reports__picker"
        />

        <!-- Sibling suggestion: same restaurant account -->
        <div
          v-if="siblingSuggestions.length"
          class="mt-2 d-flex align-center flex-wrap ga-2"
        >
          <span class="text-body-2 text-medium-emphasis">Same account:</span>
          <VChip
            v-for="r in siblingSuggestions"
            :key="r._id"
            size="small"
            variant="outlined"
            color="primary"
            prepend-icon="mdi-plus"
            @click="addSibling(r)"
          >
            {{ r.name }}
          </VChip>
        </div>

        <!-- Overseer context -->
        <p
          v-if="overseers.length"
          class="text-body-2 text-medium-emphasis mt-3 mb-0"
        >
          Overseen by
          <template
            v-for="(o, i) in overseers"
            :key="o.email"
          >
            {{ i > 0 ? ', ' : '' }}<strong>{{ o.name || o.email }}</strong> ({{ o.email }})
          </template>
        </p>

        <VDivider class="my-5" />

        <!-- 2. Options -->
        <VSwitch
          v-model="includeContact"
          color="primary"
          density="compact"
          hide-details
          label="Include member phone & email"
        />
        <p class="text-caption text-medium-emphasis mt-1 mb-0">
          Off by default — the PDF can be forwarded. Phone numbers only appear for
          members with at least one booking, matching the app.
        </p>

        <VBtn
          variant="text"
          size="small"
          class="mt-3 px-0"
          @click="showThresholds = !showThresholds"
        >
          {{ showThresholds ? 'Hide' : 'Show' }} leaderboard settings
        </VBtn>
        <VExpandTransition>
          <div
            v-show="showThresholds"
            class="d-flex flex-wrap ga-4 mt-2"
          >
            <VTextField
              v-model.number="minRequestsYtd"
              type="number"
              min="1"
              label="Min requests — YTD boards"
              density="compact"
              style="max-width: 220px;"
              hide-details
            />
            <VTextField
              v-model.number="minRequestsL3M"
              type="number"
              min="1"
              label="Min requests — 3-month boards"
              density="compact"
              style="max-width: 220px;"
              hide-details
            />
          </div>
        </VExpandTransition>
      </VCardText>

      <VDivider />

      <VCardActions class="pa-4">
        <div class="flex-grow-1">
          <VAlert
            v-if="generateError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-0"
          >
            {{ generateError }}
          </VAlert>
          <span
            v-else-if="lastGenerated"
            class="text-body-2 text-success"
          >
            Downloaded {{ lastGenerated }}
          </span>
        </div>
        <VBtn
          color="primary"
          variant="flat"
          size="large"
          :loading="generating"
          :disabled="selectedIds.length === 0"
          prepend-icon="mdi-file-pdf-box"
          @click="generateReport"
        >
          Generate &amp; Download PDF
        </VBtn>
      </VCardActions>
    </VCard>
  </VContainer>
</template>

<style lang="scss" scoped>
// iOS zooms on focus when an input renders under 16px.
.admin-reports__picker :deep(input) {
  font-size: 16px;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  # Platform-admin-only tool: generates restaurant-facing PDF reports that can
  # include member data, so the route is gated in addition to the API's
  # requireAdmin() on every /admin/reports endpoint.
  requiresAdmin: true
</route>
